import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ShieldCheck, ShieldX, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'tenants',     label: 'Tenants' },
  { key: 'owners',      label: 'Owners' },
  { key: 'consultants', label: 'Consultants' },
  { key: 'agents',      label: 'Agents' },
];

export default function AdminKYC() {
  const [data, setData] = useState({ tenants: [], owners: [], consultants: [], agents: [], total_pending: 0 });
  const [decision, setDecision] = useState(null);  // { entity_type, entity, action }
  const [remarks, setRemarks] = useState('');

  const load = useCallback(() => api.get('/kyc/queue').then((r) => setData(r.data)).catch(() => toast.error('Failed to load KYC queue')), []);
  useEffect(() => { load(); }, [load]);

  const open = (entity_type, entity, action) => { setDecision({ entity_type, entity, action }); setRemarks(''); };

  const submitDecision = async () => {
    if (!decision) return;
    if (decision.action === 'rejected' && !remarks.trim()) return toast.error('Please add rejection remarks');
    try {
      await api.post(`/kyc/${decision.entity_type}/${decision.entity.id}/decision`, {
        decision: decision.action,
        remarks: remarks.trim(),
      });
      toast.success(`KYC ${decision.action}`);
      setDecision(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <PageHeader title="KYC Approvals" subtitle={`${data.total_pending || 0} pending submission(s) across all user types`} />

      <Tabs defaultValue="tenants">
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`kyc-tab-${t.key}`}>
              {t.label} <Badge variant="outline" className="ml-2 text-[10px]">{(data[t.key] || []).length}</Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-3">
            <Card className="p-0 overflow-x-auto">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="bg-muted/60">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Name</th>
                    <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Email</th>
                    <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Mobile</th>
                    <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Status</th>
                    <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Submitted</th>
                    <th className="px-3 py-2 text-xs uppercase text-muted-foreground text-right">Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {(data[t.key] || []).length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-12 text-center text-muted-foreground">
                      <Inbox className="h-6 w-6 mx-auto mb-2 text-muted-foreground/60" />
                      No pending {t.label.toLowerCase()} KYC at the moment.
                    </td></tr>
                  )}
                  {(data[t.key] || []).map((e) => (
                    <tr key={e.id} className="border-t" data-testid={`kyc-row-${t.key}-${e.id}`}>
                      <td className="px-3 py-2 font-medium">{e.name}</td>
                      <td className="px-3 py-2">{e.email || '—'}</td>
                      <td className="px-3 py-2">{e.mobile || '—'}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 capitalize">{e.kyc_status}</Badge></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-700 hover:text-emerald-800" onClick={() => open(t.key, e, 'approved')} data-testid={`kyc-approve-${e.id}`}>
                          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:text-destructive" onClick={() => open(t.key, e, 'rejected')} data-testid={`kyc-reject-${e.id}`}>
                          <ShieldX className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decision?.action === 'approved' ? 'Approve KYC' : 'Reject KYC'}</DialogTitle></DialogHeader>
          {decision && (
            <div className="space-y-3 text-sm">
              <div>You are about to <span className={decision.action === 'approved' ? 'text-emerald-700 font-medium' : 'text-destructive font-medium'}>{decision.action}</span> KYC for <strong>{decision.entity.name}</strong>.</div>
              <div>
                <label className="text-xs text-muted-foreground">Remarks {decision.action === 'rejected' && <span className="text-destructive">*</span>}</label>
                <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} placeholder={decision.action === 'rejected' ? 'Reason for rejection (mandatory)' : 'Optional remarks'} data-testid="kyc-remarks-input" />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDecision(null)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={submitDecision} className="w-full sm:w-auto" data-testid="kyc-decision-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
